/* eslint-disable react-hooks/exhaustive-deps */

import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { formValueSelector } from "redux-form";

import {
  Edit,
  MultiCardForm,
  FormDataConsumer,
  ArrayInput,
  SimpleFormIterator,
  FormCard,
  TextInput,
  ReferenceInput,
  ReferenceArrayInput,
  SelectInput,
  AutoCompleteArrayInput,
  required,
  minLength,
  showNotification,
} from "jazasoft";
import { SimpleForm } from "jazasoft/lib/mui/form/SimpleForm";
import { Role } from "../../utils/types";

import useQuery from "../../utils/useQuery";

const selector = formValueSelector("record-form");

const Div = (props) => <div />;

const inputOptions = (sm) => ({
  xs: 12,
  sm,
  fullWidth: true,
  options: { fullWidth: true },
});

const UserEdit = (props) => {
  const { data: config = {} } = useQuery({ url: "permissions/configs" });
  const { data: roleList = [] } = useQuery({ url: "roles", params: { size: 100 } });

  const dispatch = useDispatch();

  const authState = useSelector((state) => state.jazasoft.auth);
  const formData = useSelector((state) => selector(state, "apps", "tenantId", "roleId"));
  const tenants = useSelector((state) => state.jazasoft.resources["tenants"] && state.jazasoft.resources["tenants"].data);
  const permissions = useSelector((state) => state.jazasoft.resources["permissions"] && state.jazasoft.resources["permissions"].data);

  const currRoles = props.roles || [];
  const roleMaster = roleList.find((r) => r.roleId === Role.MASTER) || {};
  const roleSuperUser = roleList.find((r) => r.roleId === Role.SUPER_USER) || {};
  const roleAdmin = roleList.find((r) => r.roleId === Role.ADMIN) || {};
  let tenant = tenants && formData.tenantId && tenants[formData.tenantId];
  let appList = tenant && tenant.appList ? tenant.appList : [];
  if (currRoles.includes(Role.ADMIN) || currRoles.includes(Role.SUPER_USER)) {
    appList = authState.appList ? authState.appList : [];
    tenant = authState.tenant;
  }
  let permissionList = appList?.flatMap((app) => config[app.appId] && config[app.appId].map((p) => ({ ...p, id: app.id, appId: app.appId }))) || [];
  const permissionKeys = permissionList.filter((p) => p && p.type === "PER_USER");

  const format = React.useCallback(
    (record) => {
      if (!record) return record;

      if (record.roleList) {
        const roleIds = record.roleList.map((r) => r.roleId);
        if (roleIds.length === 1 && roleIds.includes(roleMaster.id)) {
          record = { ...record, authorities: null, roleId: roleIds[0], apps: undefined };
        } else if (roleIds.length === 1 && roleIds.includes(roleSuperUser.id)) {
          record = { ...record, authorities: null, roleId: roleIds[0], apps: undefined };
        } else {
          record = {
            ...record,
            authorities: null,
            apps: record.appList.map((app) => ({
              ...app,
              roleIds: record.roleList.filter((r) => r.appId === app.id).map((r) => r.roleId),
              ...(config[app.appId]
                ? config[app.appId]
                    .filter((p) => p.type === "PER_USER")
                    .reduce(
                      (acc, el) => ({
                        ...acc,
                        [el.multiSelect ? `${el.key}s` : el.key]: el.multiSelect
                          ? record.permissionList.filter((p) => p.app && p.app.id === app.id && p.key === el.key).map((p) => p.id)
                          : record.permissionList.filter((p) => p.app && p.app.id === app.id && p.key === el.key).map((p) => p.id)[0],
                      }),
                      {}
                    )
                : {}),
            })),
          };
        }
      }
      return record;
    },
    [roleMaster, roleSuperUser, config]
  );

  // Remove Permissions if role is Admin
  const parse = ({ apps, ...record }) => {
    let appList = apps?.map(({ id, roleIds, ...rest }) => {
      const rIds =
        roleIds
          ?.map((id) => roleList.find((r) => r.id === id))
          .filter((e) => !!e)
          .map((r) => r.roleId) || [];
      if (rIds.includes(Role.ADMIN)) {
        return { id, roleIds };
      }
      return { id, roleIds, ...rest };
    });
    return { ...record, apps: appList };
  };

  // Do not allow multiple roles if Admin role is selected
  const validate = (values = {}) => {
    const { apps = [] } = values;
    let hasError = false;
    const appList = apps.map((app) => {
      const roleIds =
        app.roleIds
          ?.map((id) => roleList.find((r) => r.id === id))
          .filter((e) => !!e)
          .map((r) => r.roleId) || [];
      if (roleIds.includes(Role.ADMIN) && roleIds.length > 1) {
        hasError = true;
        return { roleIds: "User cannot have any other role along with Admin Role" };
      }
      return {};
    });
    if (hasError) {
      dispatch(showNotification("User cannot have any other role along with Admin Role.", { type: "danger" }));
    }
    let errors = { apps: appList };
    return errors;
  };

  return (
    <Edit cardWrapper={false} format={format} parse={parse} {...props}>
      <MultiCardForm validate={validate}>
        <FormCard title="User Info">
          <TextInput source="fullName" validate={[required(), minLength(2)]} {...inputOptions(3)} />
          <TextInput source="username" validate={[required()]} {...inputOptions(3)} />
          <TextInput source="email" {...inputOptions(3)} />
          <TextInput source="mobile" validate={[required()]} {...inputOptions(3)} />

          {currRoles.includes(Role.MASTER) && (
            <SelectInput
              source="roleId"
              choices={roleList.filter((r) => ["master", "super_user"].includes(r.roleId))}
              allowEmpty
              {...inputOptions(3)}
            />
          )}
          {(!formData.roleId || formData.roleId !== roleMaster.id) && currRoles.includes(Role.MASTER) && (
            <ReferenceInput
              source="tenantId"
              reference="tenants"
              validate={[required()]}
              {...inputOptions(3)}
              options={{ fullWidth: true, disabled: true }}
            >
              <SelectInput />
            </ReferenceInput>
          )}
        </FormCard>

        {!formData.roleId ? (
          <FormCard title="Apps">
            <ArrayInput source="apps" fullWidth={true} xs={12}>
              <SimpleFormIterator>
                <SelectInput label="App" source="id" choices={appList} {...inputOptions(3)} />

                <FormDataConsumer {...inputOptions(3)}>
                  {({ formData, source }) => {
                    let idx = source && source.split(".")[0].substring(5);
                    idx = idx.substring(0, idx.length - 1);
                    const appId = formData.apps && formData.apps[idx] && formData.apps[idx].id;
                    const choices = roleList.filter((r) => r.roleId === Role.ADMIN || r.appId === appId);

                    return (
                      <AutoCompleteArrayInput
                        label="Roles"
                        source={`${source.split(".")[0]}.roleIds`}
                        choices={choices}
                        validate={[required()]}
                        {...inputOptions(3)}
                      />
                    );
                  }}
                </FormDataConsumer>

                <FormDataConsumer {...inputOptions(12)}>
                  {({ formData, source }) => {
                    let idx = source && source.split(".")[0].substring(5);
                    idx = idx.substring(0, idx.length - 1);
                    const appId = formData.apps && formData.apps[idx] && formData.apps[idx].id;
                    const roleIds = formData.apps && formData.apps[idx] && formData.apps[idx].roleIds;
                    if (roleIds && roleIds.includes(roleAdmin.id)) return null;

                    return (
                      <SimpleForm component="div" footer={false}>
                        {permissionKeys
                          .filter((p) => p.id === appId)
                          .sort((a, b) => a.serialNo - b.serialNo)
                          .map((p) => {
                            let filter = {
                              "app.id": appId,
                              "tenant.id": tenant.id,
                              key: p.key,
                            };
                            if (p.parentKey) {
                              const parentPerm = permissionKeys.find((e) => e.key === p.parentKey);
                              const field = parentPerm && parentPerm.multiSelect ? `${p.parentKey}s` : p.parentKey;
                              const value = formData.apps[idx][field];
                              let parentValue = "-1";
                              if (value) {
                                parentValue = parentPerm.multiSelect
                                  ? value
                                      .map((id) => permissions[id])
                                      .filter((e) => e)
                                      .map((e) => e.value)
                                  : permissions[value] && permissions[value].value;
                              }
                              filter = {
                                ...filter,
                                parentValue,
                              };
                            }

                            return p.multiSelect ? (
                              <ReferenceArrayInput
                                key={`${appId}-${tenant.id}-${p.key}`}
                                label={p.name}
                                source={`${source.split(".")[0]}.${p.key}s`}
                                reference="permissions"
                                filter={filter}
                                fullWidth
                                options={{ fullWidth: true }}
                                xs={12}
                                sm={6}
                              >
                                <AutoCompleteArrayInput />
                              </ReferenceArrayInput>
                            ) : (
                              <ReferenceInput
                                key={`${appId}-${tenant.id}-${p.key}`}
                                label={p.name}
                                source={`${source.split(".")[0]}.${p.key}`}
                                reference="permissions"
                                filter={filter}
                                fullWidth
                                options={{ fullWidth: true }}
                                xs={12}
                                sm={3}
                              >
                                <SelectInput />
                              </ReferenceInput>
                            );
                          })}
                      </SimpleForm>
                    );
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </FormCard>
        ) : (
          <Div />
        )}
      </MultiCardForm>
    </Edit>
  );
};

export default UserEdit;
